// src/components/ui/Input.tsx
import React from "react";

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: React.ReactNode;
  required?: boolean;
  showRequiredMark?: boolean;
  className?: string;
  wrapperClassName?: string;
  inputClassName?: string;
  invalid?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      required,
      showRequiredMark = true,
      className,
      wrapperClassName,
      inputClassName = "",
      invalid = false,
      id,
      ...props
    },
    ref
  ) => {
    const autoId = React.useId();
    const inputId = id ?? autoId;

    // Layout + shape only — NO text styling
    const baseInput =
      "w-full h-[42px] px-4 rounded-full border bg-white focus:outline-none";

    const normalState =
      "border-[#d9d9d9] ring-0 focus:border-transparent focus:ring-2 focus:ring-black";

    const invalidState =
      "border-transparent ring-2 ring-red-600 focus:ring-2 focus:ring-red-600";

    return (
      <div
        className={`flex flex-col space-y-2.5 ${
          wrapperClassName ?? className ?? ""
        }`}
      >
        {label && (
          <label htmlFor={inputId} className="inline-flex items-center">
            {label}
            {required && showRequiredMark ? (
              <span
                aria-hidden="true"
                className="text-fuji-red leading-none relative -top-[1px]"
              >
                *
              </span>
            ) : null}
          </label>
        )}

        <div className="relative">
          <input
            id={inputId}
            ref={ref}
            {...props}
            aria-invalid={props["aria-invalid"] ?? (invalid || undefined)}
            required={required}
            className={`${baseInput} ${
              invalid ? invalidState : normalState
            } ${inputClassName}`}
          />
        </div>
      </div>
    );
  }
);

Input.displayName = "Input";
